// General
import "./add-payment-method-dialog.scss";
import { useState, useEffect, forwardRef } from "react";
// Services
import {
  useLazyGetProfileQuery,
  useLazyGetPaymentGatewayListQuery,
  useInitXenopayPaymentMutation,
  useInitAlphapoPaymentMutation,
  useInitGenesisPaymentMutation,
  useInitCardpayPaymentMutation,
  useGenesisPurchaseMutation,
  useCentrobillPurchaseMutation,
  useCentrobillTokenizationMutation,
} from "../../../../services/data.service";
import { spiApi } from "../../../../services/data.service";
import { trackingService } from "../../../../services/tracking.service";
// Static Data
import routeConst from "../../../../const/routeConst";
// Redux
import { useSelector, useDispatch } from "react-redux";
import {
  resetCreditCardDetails,
  updatePaymentMethodSelection,
} from "../../../../redux/store/paymentStore";
import {
  updateAddPaymentMethodDialog,
  updateAddCardDialog,
  updatePaymentProcessDialog,
  updateBuyCoinsDialog,
  updateCoinsPackageDialog,
  updateThankYouForPurchaseDialog,
  updateEkycPendingDialog,
} from "../../../../redux/store/dialogStore";
import { updateErrorToast } from "../../../../redux/store/toastStore";
// Material UI
import {
  Dialog,
  Slide,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
// react-gtm-module
import TagManager from "react-gtm-module";
// Material UI
import { useMediaQuery } from "@mui/material";
// i18next
import { useTranslation, Trans } from "react-i18next";
// Custom Hooks
// import useSecurionCreateCardFlow from "../../../utility/custom-hooks/useSecurionCreateCardFlow-hook";
import useSecurionCoinsPayment from "../../../utility/custom-hooks/useSecurionCoinsPayment-hook";
import useCustomNavigate from "../../../utility/custom-hooks/useCustomNavigate-hook";
import IconManager from "../../../utility/manager/icon-manager/icon-manager";
// Components
import Spinner from "../../elements/spinner/spinner";
import PaymentMethodSelection from "../../payment/payment-method-selection/payment-method-selection";
import CardDetails from "../../../shared/payment/card-details/card-details";
import AddCardLoadingDialog from "../../../shared/dialog-content/add-card-loading-dialog/add-card-loading-dialog";

const AddPaymentMethodDialog = () => {
  // API variables
  const [
    getProfile,
    {
      data: getProfileData,
      error: getProfileErrorData,
      isFetching: getProfileFetching,
      isLoading: getProfileLoading,
      isSuccess: getProfileSuccess,
      isError: getProfileError,
    },
  ] = useLazyGetProfileQuery();
  const [
    getPaymentGatewayList,
    {
      data: paymentGatewayListData,
      error: paymentGatewayListErrorData,
      isFetching: paymentGatewayListFetching,
      isLoading: paymentGatewayListLoading,
      isSuccess: paymentGatewayListSuccess,
      isError: paymentGatewayListError,
    },
  ] = useLazyGetPaymentGatewayListQuery();
  const [
    initXenopayPayment,
    {
      data: initXenopayPaymentData,
      error: initXenopayPaymentErrorData,
      isLoading: initXenopayPaymentLoading,
      isSuccess: initXenopayPaymentSuccess,
      isError: initXenopayPaymentError,
    },
  ] = useInitXenopayPaymentMutation();
  const [
    initAlphapoPayment,
    {
      data: initAlphapoPaymentData,
      error: initAlphapoPaymentErrorData,
      isLoading: initAlphapoPaymentLoading,
      isSuccess: initAlphapoPaymentSuccess,
      isError: initAlphapoPaymentError,
    },
  ] = useInitAlphapoPaymentMutation();
  const [
    initGenesisPayment,
    {
      data: initGenesisPaymentData,
      error: initGenesisPaymentErrorData,
      isLoading: initGenesisPaymentLoading,
      isSuccess: initGenesisPaymentSuccess,
      isError: initGenesisPaymentError,
    },
  ] = useInitGenesisPaymentMutation();
  const [
    initCardpayPayment,
    {
      data: initCardpayPaymentData,
      error: initCardpayPaymentErrorData,
      isLoading: initCardpayPaymentLoading,
      isSuccess: initCardpayPaymentSuccess,
      isError: initCardpayPaymentError,
    },
  ] = useInitCardpayPaymentMutation();
  const [
    genesisPurchase,
    {
      data: genesisPurchaseData,
      error: genesisPurchaseErrorData,
      isLoading: genesisPurchaseLoading,
      isSuccess: genesisPurchaseSuccess,
      isError: genesisPurchaseError,
    },
  ] = useGenesisPurchaseMutation();
  const [
    centrobillPurchase,
    {
      data: centrobillPurchaseData,
      error: centrobillPurchaseErrorData,
      isLoading: centrobillPurchaseLoading,
      isSuccess: centrobillPurchaseSuccess,
      isError: centrobillPurchaseError,
    },
  ] = useCentrobillPurchaseMutation();
  const [
    centrobillTokenization,
    {
      data: centrobillTokenizationData,
      error: centrobillTokenizationErrorData,
      isLoading: centrobillTokenizationLoading,
      isSuccess: centrobillTokenizationSuccess,
      isError: centrobillTokenizationError,
    },
  ] = useCentrobillTokenizationMutation();

  // General variables
  const [tnc, setTnc] = useState(false);
  const [xenopayMethodId, setXenopayMethodId] = useState("");

  // Redux variables
  const addPaymentMethodDialog = useSelector(
    (state) => state.dialog.addPaymentMethodDialog
  );
  const selectedCoinPackage = useSelector(
    (state) => state.products.selectedCoinPackage
  );
  const paymentMethodSelection = useSelector(
    (state) => state.payment.paymentMethodSelection
  );
  const isRedirect = useSelector((state) => state.payment.isRedirect);
  const cardNumber = useSelector((state) => state.payment.cardNumber);
  const expiryDate = useSelector((state) => state.payment.expiryDate);
  const cardHolderName = useSelector((state) => state.payment.cardHolderName);
  const cvv = useSelector((state) => state.payment.cvv);
  const country = useSelector((state) => state.payment.country);
  const isFormValid = useSelector((state) => state.payment.isFormValid);
  const promoCode = useSelector((state) => state.products.promoCode);
  const dispatch = useDispatch();

  // MUI variables
  const isMobile = useMediaQuery("(max-width: 720px)");

  // i18next variables
  const { t } = useTranslation();

  // Custom Hooks Functions
  // const securionCreateCardFlow = useSecurionCreateCardFlow();
  const securionCoinsPayment = useSecurionCoinsPayment();
  const onNavigate = useCustomNavigate();
  const getIcon = IconManager();

  // Lifecycle | Mounted
  useEffect(() => {
    if (!addPaymentMethodDialog) return;

    TagManager.dataLayer({
      dataLayer: {
        event: "PWA-AddPaymentMethod-Dialog",
      },
    });

    getProfile(null, true);
    getPaymentGatewayList("?product_type=coin", true);

    dispatch(resetCreditCardDetails());
    setTnc(false);
    setXenopayMethodId("");
  }, [addPaymentMethodDialog]);

  // Lifecycle | Check for update | Init XenoPay API Response
  useEffect(() => {
    if (initXenopayPaymentLoading) {
    } else if (initXenopayPaymentSuccess) {
      window.open(initXenopayPaymentData?.data?.url, "_self");
    } else if (initXenopayPaymentError) {
      const toastObj = {
        message: initXenopayPaymentErrorData?.data?.message,
        autoClose: 3000,
      };
      dispatch(updateErrorToast(toastObj));
    }
  }, [
    initXenopayPaymentLoading,
    initXenopayPaymentSuccess,
    initXenopayPaymentError,
  ]);

  // Lifecycle | Check for update | Init Alphapo API Response
  useEffect(() => {
    if (initAlphapoPaymentLoading) {
    } else if (initAlphapoPaymentSuccess) {
      switch (initAlphapoPaymentData?.status) {
        case 1:
          window.open(initAlphapoPaymentData?.data?.url, "_self");
          break;
        default:
          break;
      }
    } else if (initAlphapoPaymentError) {
    }
  }, [
    initAlphapoPaymentLoading,
    initAlphapoPaymentSuccess,
    initAlphapoPaymentError,
  ]);

  // Lifecycle | Check for update | initGenesisPayment API Response
  useEffect(() => {
    if (initGenesisPaymentLoading) {
    } else if (initGenesisPaymentSuccess) {
      if (initGenesisPaymentData?.data?.url) {
        window.open(initGenesisPaymentData?.data?.url, "_self");
      }
    } else if (initGenesisPaymentError) {
      const obj = {
        message: initGenesisPaymentData?.data?.message,
        autoClose: 3000,
      };
      dispatch(updateErrorToast(obj));
    }
  }, [
    initGenesisPaymentLoading,
    initGenesisPaymentSuccess,
    initGenesisPaymentError,
  ]);

  // Lifecycle | Check for update | initCardpayPayment API Response
  useEffect(() => {
    if (initCardpayPaymentLoading) {
    } else if (initCardpayPaymentSuccess) {
      if (initCardpayPaymentData?.data?.url) {
        window.open(initCardpayPaymentData?.data?.url, "_self");
      }
    } else if (initCardpayPaymentError) {
      const obj = {
        message: initCardpayPaymentErrorData?.data?.message,
        autoClose: 3000,
      };
      dispatch(updateErrorToast(obj));
    }
  }, [
    initCardpayPaymentLoading,
    initCardpayPaymentSuccess,
    initCardpayPaymentError,
  ]);

  // Lifecycle | Check for update | genesisPurchase API Response
  useEffect(() => {
    if (genesisPurchaseLoading) {
    } else if (genesisPurchaseSuccess) {
      // dispatch(updateAddCardDialog(false));
      dispatch(updatePaymentProcessDialog(false));

      switch (genesisPurchaseData?.status) {
        case 1:
          // Close buy coin dialog
          dispatch(updateBuyCoinsDialog(false));
          // Close coins package dialog
          dispatch(updateCoinsPackageDialog(false));
          // Open thank you dialog
          dispatch(updateThankYouForPurchaseDialog(true));

          dispatch(updateAddPaymentMethodDialog(false));

          trackingService.pushPurchaseEvent(
            genesisPurchaseData?.data?.currency,
            genesisPurchaseData?.data?.amount
          );

          dispatch({
            type: `${spiApi.reducerPath}/invalidateTags`,
            payload: ["CoinPayment"],
          });
          break;
        case -1:
          const errorToastObj = {
            message: genesisPurchaseData?.message,
            autoClose: 3000,
          };
          dispatch(updateErrorToast(errorToastObj));
        default:
          // dispatch(updateAddCardDialog(false));
          dispatch(updatePaymentProcessDialog(false));
          break;
      }
    } else if (genesisPurchaseError) {
      // dispatch(updateAddCardDialog(false));
      dispatch(updatePaymentProcessDialog(false));
    }
  }, [genesisPurchaseLoading, genesisPurchaseSuccess, genesisPurchaseError]);

  // Lifecycle | Check for update | centrobillPurchase API Response
  useEffect(() => {
    if (centrobillPurchaseLoading) {
    } else if (centrobillPurchaseSuccess) {
      switch (centrobillPurchaseData?.status) {
        case 0:
        case 1:
          if (centrobillPurchaseData?.data?.url) {
            window.open(centrobillPurchaseData?.data?.url, "_self");
          } else {
            dispatch(updateBuyCoinsDialog(false));
            dispatch(updateCoinsPackageDialog(false));
            onCloseDialog();
            onNavigate(
              routeConst.payment.centrobill.path,
              false,
              `?status=successful&txn_id=${centrobillPurchaseData?.data?.txn_id}`
            );
          }
          break;
        default:
          // const errorToastObj = {
          //   message: centrobillPurchaseData?.message,
          //   autoClose: 3000,
          // };
          // dispatch(updateErrorToast(errorToastObj));

          // Recall GET Payment Gateway API
          dispatch({
            type: `${spiApi.reducerPath}/invalidateTags`,
            payload: ["GetPaymentGatewayList"],
          });

          // dispatch(updatePaymentMethodSelection(null));

          onCloseDialog();

          onNavigate(
            routeConst.payment.centrobill.path,
            false,
            "?status=failed&txn_id=123"
          );
          break;
      }
    } else if (centrobillPurchaseError) {
    }
  }, [
    centrobillPurchaseLoading,
    centrobillPurchaseSuccess,
    centrobillPurchaseError,
  ]);

  // Lifecycle | Check for update | centrobillTokenization API Response
  useEffect(() => {
    if (centrobillTokenizationLoading) {
    } else if (centrobillTokenizationSuccess) {
      if (centrobillTokenizationData?.token) {
        const centrobillObj = {
          product_hash_id: selectedCoinPackage?.product_hash_id,
          payment_token: centrobillTokenizationData?.token,
          redirect_url: `${window.origin}${routeConst.payment.centrobill.path}`,
          promo_code: promoCode,
        };
        centrobillPurchase(centrobillObj);
      } else {
        const errorToastObj = {
          message: centrobillTokenizationData?.message,
          autoClose: 3000,
        };
        dispatch(updateErrorToast(errorToastObj));
      }
    } else if (centrobillTokenizationError) {
      const errorToastObj = {
        message: t("misc.centrobill_tokenization_error"),
        // message: centrobillTokenizationErrorData?.data?.message,
        autoClose: 3000,
      };
      dispatch(updateErrorToast(errorToastObj));
    }
  }, [
    centrobillTokenizationLoading,
    centrobillTokenizationSuccess,
    centrobillTokenizationError,
  ]);

  // Event Handlers | Button
  const onAddCard = () => {
    if (getProfileData?.data?.verifications?.ekyc?.pending_approval) {
      dispatch(updateEkycPendingDialog(true));
    } else if (
      !getProfileData?.data?.verifications?.ekyc?.verified &&
      getProfileData?.data?.verifications?.ekyc?.verification_required
    ) {
      onNavigate(routeConst.verify.ekyc.onboarding.path);
      onCloseDialog();
    } else {
      switch (paymentMethodSelection) {
        case "securionpay":
          if (!isFormValid || !tnc) return;

          // securionCreateCardFlow();
          securionCoinsPayment();
          break;
        case "xenopay":
          if (!tnc || initXenopayPaymentLoading || !isXenopayMethodValid)
            return;

          const xenopayObj = {
            product_hash_id: selectedCoinPackage?.product_hash_id,
            product_type: selectedCoinPackage?.product_name,
            payment_id: xenopayMethodId || undefined,
            redirect_url: `${window.origin}${routeConst.payment.redirect.path}`,
          };
          initXenopayPayment(xenopayObj);
          break;
        case "alphapo":
          if (!tnc || initAlphapoPaymentLoading) return;

          const alphapoObj = {
            product_hash_id: selectedCoinPackage?.product_hash_id,
            product_type: selectedCoinPackage?.product_name,
            redirect_url: `${window.origin}${routeConst.payment.alphapoRedirect.path}`,
            // redirect_url: `http://localhost${routeConst.payment.alphapoRedirect.path}`,
          };
          initAlphapoPayment(alphapoObj);
          break;
        case "genesis":
          if (!tnc || returnShowSpinner()) return;

          if (isRedirect) {
            const genesisObj = {
              payment_tag: "purchase-coins",
              product_hash_id: selectedCoinPackage?.product_hash_id,
              product_type: "coin",
              promo_code: promoCode,
              redirect_url: `${window.origin}${routeConst.payment.genesis.path}`,
              // redirect_url: `http://localhost${routeConst.payment.genesis.path}`,
            };
            initGenesisPayment(genesisObj);
          } else {
            const obj = {
              payment_tag: "",
              product_hash_id: selectedCoinPackage?.product_hash_id,
              // payment_token: saveGenesisCardData?.data?.token,
              card_number: cardNumber,
              expiry_month: expiryDate?.month,
              expiry_year: expiryDate?.fullYear?.toString(),
              first_name: cardHolderName?.firstName,
              last_name: cardHolderName?.lastName,
              cvv: cvv,
              country: country?.country_iso_code,
              promo_code: promoCode,
              redirect_url: `${window.origin}${routeConst.payment.genesis.path}`,
            };
            genesisPurchase(obj);
            // dispatch(updateAddCardDialog(true));
            dispatch(updatePaymentProcessDialog(true));
          }
          break;
        case "cardpay":
          if (!tnc || initCardpayPaymentLoading) return;

          const cardpayObj = {
            product_hash_id: selectedCoinPackage?.product_hash_id,
            product_type: selectedCoinPackage?.product_name,
            promo_code: promoCode,
            redirect_url: `${window.origin}${routeConst.payment.cardpay.path}`,
            // redirect_url: `https://dev.sugarbook.club${routeConst.payment.cardpay.path}`,
          };
          initCardpayPayment(cardpayObj);
          break;
        case "centrobill":
          if (!isFormValid || !tnc) return;

          const centrobillTokenObj = {
            number: cardNumber,
            expirationYear: expiryDate?.year,
            expirationMonth: expiryDate?.month,
            cvv: cvv,
            cardHolder:
              cardHolderName?.firstName + " " + cardHolderName?.lastName,
          };
          centrobillTokenization(centrobillTokenObj);
          break;
        default:
          break;
      }
    }
  };

  // Event Handlers | MUI Checkbox
  const onCheckboxChange = (event) => {
    setTnc(event.target.checked);
  };

  // Event Handlers | MUI Dialog
  const onCloseDialog = () => {
    dispatch(updatePaymentMethodSelection(null));
    dispatch(updateAddPaymentMethodDialog(false));
  };

  // Utility Functions
  const xenopayMethods =
    paymentGatewayListData?.data?.payment_methods?.find(
      (gateway) => gateway?.provider === "xenopay"
    )?.payment_methods ?? [];
  // Only require a pick when the API actually offers sub-methods
  const isXenopayMethodValid =
    paymentMethodSelection !== "xenopay" ||
    xenopayMethods.length === 0 ||
    xenopayMethodId !== "";

  const returnShowSpinner = () => {
    switch (paymentMethodSelection) {
      case "securionpay":
        return false;
      case "xenopay":
        return initXenopayPaymentLoading;
      case "alphapo":
        return initAlphapoPaymentLoading;
      case "xenopay":
        return initXenopayPaymentLoading;
      case "genesis":
        return genesisPurchaseLoading || initGenesisPaymentLoading;
      case "centrobill":
        return centrobillTokenizationLoading || centrobillPurchaseLoading;
      default:
        return false;
    }
  };
  return (
    <Dialog
      className={
        isMobile ? "custom-bottom-sheet-no-radius-fullscreen-dialog" : ""
      }
      fullScreen={isMobile ? true : false}
      open={addPaymentMethodDialog}
      TransitionComponent={Transition}
      onClose={onCloseDialog}
    >
      <div id="add-payment-method-dialog">
        <div className="dialog-header">
          <div className="close-button-container" onClick={onCloseDialog}>
            <CloseIcon className="close-button" />
          </div>
        </div>

        <div className="padding-container">
          <Divider />

          <div className="coin-package-selected-container">
            <div className="sb-coin-icon-container">
              {getIcon("sbCoinIcon", "sb-coin-icon")}
            </div>

            <div className="coin-amount">{selectedCoinPackage?.title}</div>

            <div className="payment-amount-container">
              <div className="payment-amount">
                {selectedCoinPackage?.currency}
                {selectedCoinPackage?.discounted_amount?.toLocaleString() ??
                  selectedCoinPackage?.amount?.toLocaleString()}
              </div>

              <div className="tax-label">
                {selectedCoinPackage?.tax_label
                  ? `(${t("purchase.includes_tax_alt", {
                      taxValue: selectedCoinPackage?.tax_percentage,
                      taxType: selectedCoinPackage?.tax_label,
                    })})`
                  : `(${t("purchase.includes_tax")})`}
              </div>
            </div>
          </div>

          <Divider />

          <PaymentMethodSelection
            availableGateways={selectedCoinPackage?.gateways}
            xenopayMethodId={xenopayMethodId}
            onXenopayMethodChange={setXenopayMethodId}
          />

          {(paymentMethodSelection === "securionpay" ||
            paymentMethodSelection === "centrobill" ||
            (paymentMethodSelection === "genesis" && !isRedirect)) && (
            <CardDetails
              showCountry={paymentMethodSelection !== "centrobill"}
            />
          )}

          {paymentMethodSelection === "securionpay" && (
            <div className="we-accept-label">{t("purchase.we_accept")}:</div>
          )}

          {paymentMethodSelection === "securionpay" && (
            <div className="credit-card-company-image-container">
              {getIcon("visaLogo", "visa-logo")}
              {getIcon("mastercardLogo", "mastercard-logo")}
              {getIcon("maestroLogo", "maestro-logo")}
              {getIcon("visaElectronLogo", "electron-logo")}
            </div>
          )}

          {paymentMethodSelection && (
            <div className="agreement-checkbox-container">
              <FormGroup className="checkbox">
                <FormControlLabel
                  control={
                    <Checkbox
                      className="custom-checkbox"
                      onChange={onCheckboxChange}
                    />
                  }
                  label={t("purchase.i_accept_tnc")}
                />
              </FormGroup>
            </div>
          )}

          {paymentMethodSelection && (
            <div className="discretion-container">
              <div className="discretion-head">
                {paymentGatewayListData?.data?.copywriting?.descriptor?.head}
              </div>
              <div className="discretion-body">
                {paymentGatewayListData?.data?.copywriting?.descriptor?.body?.join(
                  ", "
                )}
              </div>
            </div>
          )}

          {paymentMethodSelection && (
            <div
              className={`pay-button ${
                isFormValid && tnc && isXenopayMethodValid
                  ? ""
                  : "disabled-button"
              }`}
              onClick={onAddCard}
            >
              {returnShowSpinner() ? (
                <Spinner size={20} isPadding={false} color={"white-spinner"} />
              ) : (
                t("common.continue")
              )}
            </div>
          )}

          {paymentMethodSelection && (
            <footer className="payment-terms">
              <Trans
                i18nKey="purchase.payment_terms"
                defaults="By proceeding with payment, you agree to this platform's <terms>Terms & Condition</terms>."
                components={{
                  terms: (
                    <a
                      href={routeConst.terms.pathAlt}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Terms &amp; Condition
                    </a>
                  ),
                }}
              />
            </footer>
          )}
        </div>
      </div>
    </Dialog>
  );
};

export default AddPaymentMethodDialog;

// Render Functions | MUI Dialog | Will not work if inside of the same render as the Dialog
const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});
